import React from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import ProcedurePlaceholder from "@/components/ProcedurePlaceholder";
import { HERO } from "@/data/procedureHeroImages";
import PatientTestimonials from "../components/PatientTestimonials";
import SEO from "@/components/SEO";
import { WHATSAPP_URL, TEL_URL, PHONE_DISPLAY } from "@/constants/contact";
import {
  ArrowRight,
  Award,
  ChevronRight,
  Clock,
  GraduationCap,
  MessageCircle,
  Phone,
  Minus,
  Monitor,
  Plus,
  CheckCircle2,
  Sparkles,
  CircleDot,
  Footprints,
  Layers,
  ScanLine,
  Syringe,
  HeartPulse,
  Zap,
  Flame,
  Snowflake,
  Scissors,
  Droplets,
} from "lucide-react";

const PAGE_URL = "https://drvickyjain.com/wart-removal-mumbai";
const SKIN_TAG_PATH = "/skin-tag-removal-mumbai";
const CORN_PATH = "/corn-removal-treatment-mumbai";
const MOLE_PATH = "/mole-removal-mumbai";

const WART_FAQS = [
  {
    question: "Do warts go away on their own?",
    answer:
      "Some warts, particularly in children and young adults with healthy immune systems, eventually resolve without treatment as the immune system mounts a response to the HPV virus. This can take months to years and is unpredictable. Many warts persist indefinitely and spread to adjacent areas during the waiting period. For warts that are painful, spreading, or cosmetically prominent, treatment is more appropriate than observation.",
  },
  {
    question: "Will a wart come back after removal?",
    answer:
      "The removed wart does not return if treatment is complete. However, the HPV virus can remain in the surrounding skin and cause new warts to develop nearby after the visible growth has been treated. This is more common than recurrence after benign lesion removal because the viral cause is not entirely eliminated by removing the visible wart. Multiple treatment sessions reduce residual viral burden and decrease the likelihood of new wart formation in the treated area.",
  },
  {
    question: "Can I use home treatment products to remove warts?",
    answer:
      "Over-the-counter salicylic acid products can be effective for small, early common warts on the hands with consistent daily use over several weeks. They are less effective for plantar warts, facial warts, and warts that have been present for a long time. Improper use of home products on the face, around the eyes, or in sensitive areas risks chemical burns to surrounding skin. Home treatment is not appropriate for genital warts. Professional clinic treatment is more reliable, faster, and produces a better cosmetic outcome for most wart presentations.",
  },
  {
    question: "Is wart removal painful?",
    answer:
      "Local anesthetic is applied before most wart removal procedures. Topical numbing cream is sufficient for small warts on less sensitive areas. An anesthetic injection is used for plantar warts where the foot requires more complete anesthesia for pain-free treatment. Cryotherapy produces a cold, stinging sensation during application. Post-procedure tenderness for 1 to 3 days is managed with standard pain relief.",
  },
  {
    question: "How many sessions are needed to remove a wart?",
    answer:
      "This depends on the wart type, size, and how long it has been present. Many warts are cleared in one to two sessions of RF, electrocautery, or laser treatment. Cryotherapy typically requires two to four sessions spaced two to three weeks apart. Large or treatment-resistant warts may need more sessions. Dr. Jain advises on the expected number of sessions during consultation based on the specific wart being treated.",
  },
  {
    question: "Are warts contagious?",
    answer:
      "Yes. HPV spreads through direct skin-to-skin contact with an infected area and through contaminated surfaces. Warts on the hands can spread to the face by touching. Plantar warts spread in communal wet areas. Genital warts are transmitted sexually. Avoiding picking or scratching warts, keeping them covered in communal areas, and not sharing personal items reduces transmission to others and to other areas of the patient's own skin.",
  },
  {
    question: "What is the difference between a wart and a corn?",
    answer:
      "A corn is thickened skin with a hard central core caused by pressure over a bony area. It is not caused by a virus and cannot spread. A wart is caused by HPV, has a rough surface, and often has small black dots within it. Both can be painful on the foot. The black dots, the rough irregular surface, and the disruption of normal skin lines are the distinguishing features of a wart. Clinically treating a corn as a wart or vice versa produces the wrong treatment and poor results. Any painful foot growth should be assessed before treatment is selected.",
  },
  {
    question: "Can children have their warts treated safely?",
    answer:
      "Yes. Treatment options and technique selection are adapted for children based on age, the ability to cooperate with the procedure, and the extent of wart involvement. Cryotherapy and topical salicylic acid are often the first approach in younger children. RF and electrocautery are used for older children who can cooperate with a brief local anesthetic procedure. Many childhood warts resolve with the immune system's natural response, and observation is appropriate for asymptomatic warts that are not spreading rapidly.",
  },
];

const structuredData = {
  "@context": "https://schema.org",
  "@graph": [
    {
      "@type": "MedicalCondition",
      name: "Wart (Verruca)",
      alternateName: ["Verruca", "Wart"],
      description:
        "Benign epidermal growth caused by human papillomavirus (HPV); contagious and managed with destructive or excisional clinic techniques.",
      cause: {
        "@type": "MedicalCause",
        name: "Human Papillomavirus (HPV)",
      },
      associatedAnatomy: { "@type": "AnatomicalStructure", name: "Skin" },
      possibleTreatment: [
        "Cryotherapy",
        "Radiofrequency removal",
        "Electrocautery",
        "Laser treatment",
        "Surgical excision",
        "Medical-grade salicylic acid",
      ],
      url: PAGE_URL,
    },
    {
      "@type": "MedicalProcedure",
      name: "Wart removal",
      description:
        "Clinic-based removal or destruction of HPV-related verrucae using cryotherapy, radiofrequency, electrocautery, laser, or excision under plastic surgical care.",
      procedureType: "TherapeuticProcedure",
      bodyLocation: { "@type": "AnatomicalStructure", name: "Skin" },
      medicalSpecialty: "https://schema.org/PlasticSurgery",
      url: PAGE_URL,
    },
    {
      "@type": "FAQPage",
      mainEntity: WART_FAQS.map((faq) => ({
        "@type": "Question",
        name: faq.question,
        acceptedAnswer: { "@type": "Answer", text: faq.answer },
      })),
    },
  ],
};

function WartJsonLd() {
  return <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(structuredData) }} />;
}

function Breadcrumb() {
  const items = [
    { name: "Home", href: "/" },
    { name: "Reconstructive Surgery", href: "/reconstructive-surgery-in-mumbai" },
    { name: "Wart Removal", href: "/wart-removal-mumbai" },
  ];
  return (
    <nav className="bg-warm-white pt-24 pb-4" data-testid="breadcrumb">
      <div className="container mx-auto px-4 md:px-8 max-w-7xl">
        <ol className="flex flex-wrap items-center gap-2 text-sm text-deep-navy/60">
          {items.map((item, index) => (
            <li key={item.name} className="flex items-center gap-2">
              {index > 0 && <ChevronRight className="w-3 h-3 text-gold" />}
              {index === items.length - 1 ? (
                <span className="text-deep-navy/70">{item.name}</span>
              ) : (
                <Link to={item.href} className="hover:text-gold transition-colors">
                  {item.name}
                </Link>
              )}
            </li>
          ))}
        </ol>
      </div>
    </nav>
  );
}

function HeroSection() {
  return (
    <section className="bg-warm-white pt-8 md:pt-10 pb-12 md:pb-20" data-testid="procedure-hero">
      <div className="container mx-auto px-4 md:px-8 max-w-7xl">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-16 items-center">
          <div>
            <p className="text-xs font-bold tracking-[0.2em] text-gold uppercase mb-3">Reconstructive Surgery · Skin</p>
            <h1 className="font-serif text-4xl sm:text-5xl md:text-6xl text-premium-purple leading-[1.08] mb-4">Wart Removal</h1>
            <div className="w-12 h-[2px] bg-gold mb-5" />
            <p className="text-lg sm:text-xl text-deep-navy/65 leading-relaxed mb-7 max-w-xl font-light">
              Safe &amp; Effective HPV Wart Treatment by Dr. Vicky Jain · Plastic &amp; Reconstructive Surgeon
            </p>
            <div className="flex flex-wrap gap-2">
              <div className="flex items-center gap-2 bg-premium-purple/10 px-3 py-1.5 rounded-full">
                <Award className="w-3.5 h-3.5 text-gold flex-shrink-0" />
                <span className="text-xs font-semibold text-premium-purple whitespace-nowrap">Board Certified</span>
              </div>
              <div className="flex items-center gap-2 bg-premium-purple/10 px-3 py-1.5 rounded-full">
                <Clock className="w-3.5 h-3.5 text-gold flex-shrink-0" />
                <span className="text-xs font-semibold text-premium-purple whitespace-nowrap">17+ Years Experience</span>
              </div>
              <div className="flex items-center gap-2 bg-premium-purple/10 px-3 py-1.5 rounded-full">
                <GraduationCap className="w-3.5 h-3.5 text-gold flex-shrink-0" />
                <span className="text-xs font-semibold text-premium-purple whitespace-nowrap">International Training</span>
              </div>
            </div>
          </div>
          <div className="flex justify-center lg:justify-end">
            <ProcedurePlaceholder procedureName="Wart Removal" icon="🦠" size="hero"  imageSrc={HERO.wartRemoval} imageAlt="Wart removal"/>
          </div>
        </div>
      </div>
    </section>
  );
}

function WhatAreSection() {
  return (
    <section className="bg-warm-white py-14 md:py-20 border-t border-deep-navy/[0.06]" data-testid="what-are-section">
      <div className="container mx-auto px-4 md:px-8 max-w-7xl">
        <div className="grid lg:grid-cols-[1fr_2fr] gap-10 md:gap-16 items-start">
          <div>
            <p className="text-xs font-bold tracking-[0.2em] text-gold uppercase mb-3">Overview</p>
            <h2 className="font-serif text-3xl md:text-4xl text-premium-purple leading-tight">What are Warts?</h2>
          </div>
          <div className="space-y-6 text-deep-navy/70 leading-relaxed">
            <p>
              Warts are benign skin growths caused by infection with the Human Papillomavirus (HPV). The virus infects the superficial layers of the skin through small cuts or abrasions and causes the skin cells to proliferate abnormally, producing the characteristic rough, raised growth. Warts are not dangerous in healthy patients but they are contagious, can spread to other areas of the body and to other people through direct skin contact, and in many cases persist or multiply without treatment.
            </p>
            <p>
              The appearance of a wart depends on where it is located on the body. Warts on the hands and fingers are rough and raised. Warts on the soles of the feet are compressed inward by body weight and cause pain. Flat warts on the face and neck are small and smooth. The common feature across all types is the viral cause. This distinguishes warts from skin tags, which are caused by friction, and corns, which are caused by pressure.
            </p>
            <p>
              Dr. Vicky Jain removes warts using radiofrequency, cryotherapy, electrocautery, laser, or surgical excision depending on the wart type, size, location, and number. Treatment selection also considers whether the patient has had previous wart treatment and whether the warts are in sensitive locations requiring adapted technique.
            </p>
          </div>
        </div>
      </div>
    </section>
  );
}

function DrJainApproachSection() {
  return (
    <section className="bg-premium-purple py-10 md:py-14" data-testid="dr-jain-approach-section">
      <div className="container mx-auto px-4 md:px-8 max-w-7xl">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
          <div>
            <p className="text-xs font-bold tracking-[0.2em] text-gold uppercase mb-3">Our Approach</p>
            <h2 className="font-serif text-3xl md:text-4xl text-white mb-8">Dr. Jain&apos;s Approach to Wart Treatment</h2>
            <div className="space-y-6 text-white/75 leading-relaxed">
              <p>
                Wart treatment requires accurate diagnosis before selecting a technique. The viral nature of warts means that incomplete treatment or treatment that does not address the viral infected tissue allows the virus to persist and produce recurrence or new warts nearby. Cryotherapy and laser target viral tissue through destruction. RF and electrocautery remove the wart tissue physically. Surgical excision removes the entire wart body. Each approach has specific advantages for different wart types and locations.
              </p>
              <p>
                Recurrence after wart treatment is more common than recurrence after lipoma or cyst removal because the viral infection is systemic in the skin, not confined to a single removable structure. The HPV virus can remain in the surrounding skin and produce new warts after the visible growth has been removed. This is why multiple treatment sessions are sometimes needed and why hygiene measures alongside clinical treatment reduce recurrence rates. Dr. Jain discusses realistic expectations for each patient based on the type, extent, and duration of wart infection before treatment.
              </p>
            </div>
          </div>
          <div className="relative">
            <img src="/dr-jain.avif" alt="Dr. Vicky Jain" loading="lazy" decoding="async" className="w-full h-[420px] object-cover object-top" />
            <div
              className="absolute top-0 left-0 right-0 h-[420px] pointer-events-none hidden lg:block"
              style={{ background: "linear-gradient(to right, #3D2566 0%, rgba(61,37,102,0) 20%)" }}
            />
            <div className="mt-4 text-center lg:text-left">
              <p className="font-serif text-lg text-white/80">Dr. Vicky Jain · Plastic &amp; Reconstructive Surgeon</p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

function TypesOfWartsSection() {
  const cards = [
    {
      title: "Common Warts",
      icon: CircleDot,
      copy: "Found most frequently on the hands, fingers, and around the nails. Rough, raised, dome-shaped surface with small black dots representing thrombosed capillaries. The most frequently treated wart type. Spread by direct contact and by touching then picking at the wart.",
    },
    {
      title: "Plantar Warts",
      icon: Footprints,
      copy: "Located on the sole of the foot. Compressed inward by body weight, producing a flat or slightly depressed surface rather than a raised growth. Cause pain with walking that is often described as walking on a small stone. Black dots within the lesion confirm the diagnosis. Multiple plantar warts clustered together form a mosaic wart pattern.",
    },
    {
      title: "Flat Warts",
      icon: Layers,
      copy: "Small, smooth, flat-topped warts that occur in clusters. Common on the face, neck, and legs. Less raised than common warts and easily spread by shaving over them, which is why men often develop them in the beard area and women on the legs.",
    },
    {
      title: "Filiform Warts",
      icon: ScanLine,
      copy: "Long, narrow, thread-like projections that grow on the face, particularly around the mouth, nose, and eyelids. Tend to grow quickly and are cosmetically prominent due to their location. Respond well to snip excision or RF removal.",
    },
    {
      title: "Periungual Warts",
      icon: Syringe,
      copy: "Develop around or under the fingernails or toenails. Can distort the nail as they grow beneath it. More difficult to treat than warts in other locations due to the nail anatomy and the risk of nail damage during treatment.",
    },
    {
      title: "Genital Warts",
      icon: HeartPulse,
      copy: "Caused by specific strains of HPV distinct from those causing common warts. Require specialized assessment and management as a separate clinical entity from warts on other body sites. Dr. Jain assesses and treats genital warts with the specific precautions and management protocols appropriate to this location.",
    },
  ];
  return (
    <section className="bg-[#FAFAF8] py-14 md:py-20 border-t border-deep-navy/[0.06]" data-testid="types-of-warts-section">
      <div className="container mx-auto px-4 md:px-8 max-w-7xl">
        <div className="text-center mb-10">
          <p className="text-xs font-bold tracking-[0.2em] text-gold uppercase mb-3">Indications</p>
          <h2 className="font-serif text-3xl md:text-4xl text-premium-purple">Types of Warts and Their Characteristics</h2>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {cards.map((card) => (
            <div key={card.title} className="bg-white p-8 border border-[#E8E2D9]">
              <div className="w-12 h-12 bg-gold/10 flex items-center justify-center mb-4" aria-hidden>
                <card.icon className="w-6 h-6 text-gold" />
              </div>
              <h3 className="font-serif text-xl text-premium-purple mb-4 font-semibold tracking-tight">{card.title}</h3>
              <p className="text-deep-navy/70 leading-relaxed">{card.copy}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

const VS_THREE_ROWS = [
  {
    label: "Cause",
    wart: "Human Papillomavirus (HPV). Viral infection.",
    skinTag: "Skin friction and individual predisposition. Not viral.",
    corn: "Repeated pressure over a bony prominence.",
  },
  {
    label: "Texture",
    wart: "Rough, irregular surface. Disrupts normal skin line pattern.",
    skinTag: "Soft, smooth, hangs on a stalk.",
    corn: "Hard central core in thickened skin.",
  },
  {
    label: "Contagious",
    wart: "Yes. Spreads by direct skin contact.",
    skinTag: "No. Cannot spread.",
    corn: "No. Cannot spread.",
  },
  {
    label: "Pain",
    wart: "Sometimes. Plantar warts on the foot are often painful.",
    skinTag: "Painless unless traumatized.",
    corn: "Painful with pressure.",
  },
  {
    label: "Black Dots",
    wart: "Often present. Thrombosed capillaries within the wart.",
    skinTag: "Absent.",
    corn: "Absent.",
  },
  {
    label: "Treatment",
    wart: "Antiviral tissue destruction or excision. Multiple sessions sometimes needed.",
    skinTag: "Simple removal in a single session.",
    corn: "Paring or surgical excision with pressure cause correction.",
  },
];

function WartVsOthersSection() {
  return (
    <section className="bg-warm-white py-14 md:py-20 border-t border-deep-navy/[0.06]" data-testid="wart-vs-others-section">
      <div className="container mx-auto px-4 md:px-8 max-w-7xl">
        <div className="text-center mb-10">
          <p className="text-xs font-bold tracking-[0.2em] text-gold uppercase mb-3">Understanding Your Options</p>
          <h2 className="font-serif text-3xl md:text-4xl text-premium-purple">Warts vs. Skin Tags vs. Corns</h2>
        </div>
        <div className="max-w-6xl mx-auto overflow-x-auto">
          <table className="w-full text-left border-collapse text-xs sm:text-sm md:text-base min-w-[640px]">
            <thead>
              <tr className="border-b border-deep-navy/20">
                <th scope="col" className="py-4 pr-2 sm:pr-4 font-serif text-premium-purple font-normal w-[14%]" />
                <th scope="col" className="py-4 px-2 sm:px-3 font-serif text-premium-purple bg-white/80">
                  Wart
                </th>
                <th scope="col" className="py-4 px-2 sm:px-3 font-serif text-premium-purple bg-white/80">
                  <Link to={SKIN_TAG_PATH} className="hover:text-gold transition-colors">
                    Skin Tag
                  </Link>
                </th>
                <th scope="col" className="py-4 pl-2 sm:pl-3 font-serif text-premium-purple bg-white/80">
                  <Link to={CORN_PATH} className="hover:text-gold transition-colors">
                    Corn
                  </Link>
                </th>
              </tr>
            </thead>
            <tbody>
              {VS_THREE_ROWS.map((row) => (
                <tr key={row.label} className="border-b border-deep-navy/10">
                  <th scope="row" className="py-4 pr-2 sm:pr-4 font-medium text-premium-purple align-top">
                    {row.label}
                  </th>
                  <td className="py-4 px-2 sm:px-3 text-deep-navy/70 align-top">{row.wart}</td>
                  <td className="py-4 px-2 sm:px-3 text-deep-navy/70 align-top">{row.skinTag}</td>
                  <td className="py-4 pl-2 sm:pl-3 text-deep-navy/70 align-top">{row.corn}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <p className="max-w-6xl mx-auto mt-8 text-deep-navy/70 text-center md:text-left leading-relaxed">
          Correctly identifying the growth before treatment matters because each condition requires a different approach. A wart treated as a skin tag with simple snip excision without addressing the viral tissue has a higher recurrence rate than wart-specific treatment. Any rough-surfaced growth with black dots should be assessed as a probable wart before treatment is selected.
        </p>
      </div>
    </section>
  );
}

function SpreadPreventionSection() {
  return (
    <section className="bg-[#FAFAF8] py-14 md:py-20 border-t border-deep-navy/[0.06]" data-testid="spread-prevention-section">
      <div className="container mx-auto px-4 md:px-8 max-w-7xl">
        <div className="grid lg:grid-cols-[1fr_2fr] gap-10 md:gap-16 items-start">
          <div>
            <p className="text-xs font-bold tracking-[0.2em] text-gold uppercase mb-3">What to Expect</p>
            <h2 className="font-serif text-3xl md:text-4xl text-premium-purple leading-tight">How Warts Spread and How to Reduce Spread</h2>
          </div>
          <div className="space-y-6 text-deep-navy/70 leading-relaxed">
            <p>
              HPV spreads through direct skin-to-skin contact with an infected area and through contact with surfaces contaminated by the virus. The virus enters the skin through small cuts, abrasions, or areas of softened skin. Common transmission routes include touching someone else&apos;s wart directly, sharing towels or personal items, walking barefoot in communal areas such as swimming pool surrounds and changing rooms, and biting or picking at warts which spreads the virus to adjacent skin and to the fingers.
            </p>
            <p>
              Reducing spread while warts are present involves keeping the wart covered when in contact with others or communal surfaces, avoiding picking or scratching the wart, washing hands after touching a wart, not sharing towels, razors, or nail files with household members, and wearing footwear in communal wet areas. These measures reduce transmission but do not substitute for treatment in patients with active, spreading, or symptomatic warts.
            </p>
          </div>
        </div>
      </div>
    </section>
  );
}

const TREATMENT_OPTIONS = [
  {
    title: "Radiofrequency (RF) Removal",
    icon: Zap,
    body:
      "RF energy removes the wart tissue precisely with minimal damage to surrounding skin and good control of bleeding. Produces clean removal in a single session for most wart types. The treated area heals over 1 to 2 weeks. Effective for common warts, filiform warts, and flat warts. Appropriate for most body locations. May require more than one session for large or deep warts.",
  },
  {
    title: "Cryotherapy",
    icon: Snowflake,
    body:
      "Liquid nitrogen freezes the wart tissue to minus 196 degrees Celsius, causing cell death and gradual wart sloughing over 1 to 2 weeks. Multiple sessions spaced 2 to 3 weeks apart are typically needed for complete clearance. The most established non-surgical wart treatment. Can cause temporary hypopigmentation or blistering at the treated site. Effective for most wart types but less so for very thick plantar warts without additional paring.",
  },
  {
    title: "Electrocautery",
    icon: Flame,
    body:
      "Electric current burns the wart tissue. Fast and effective. Produces a scab that separates over 7 to 10 days. Used as an alternative to RF in appropriate cases. Similar outcomes to RF with slight differences in healing pattern.",
  },
  {
    title: "Laser Treatment",
    icon: Sparkles,
    body:
      "Pulsed dye laser targets the blood vessels supplying the wart, destroying the blood supply and causing the wart to die. Effective for warts resistant to other treatments and for warts in locations where physical removal is difficult. Multiple sessions may be required. Produces minimal scarring. Appropriate for facial warts where preserving cosmetic outcome is the priority.",
  },
  {
    title: "Surgical Excision",
    icon: Scissors,
    body:
      "Complete removal of the wart through a small incision with suture closure. Used for large, isolated, or treatment-resistant warts where complete physical removal is the most reliable approach. Produces a linear scar. The specimen is sent for histological analysis. Best reserved for warts that have not responded to other treatments.",
  },
  {
    title: "Salicylic Acid (Medical Grade)",
    icon: Droplets,
    body:
      "High-concentration salicylic acid applied in clinic softens and destroys wart tissue over repeated applications. Used as an adjunct to other treatments or as a maintenance treatment between sessions. Less effective as a standalone treatment for established warts than clinic-based techniques.",
  },
];

function TreatmentOptionsSection() {
  return (
    <section className="bg-premium-purple py-10 md:py-14" data-testid="treatment-options-section">
      <div className="container mx-auto px-4 md:px-8 max-w-7xl">
        <div className="text-center mb-10">
          <p className="text-xs font-bold tracking-[0.2em] text-gold uppercase mb-3">Procedure Options</p>
          <h2 className="font-serif text-3xl md:text-4xl text-white">Wart Removal Treatment Options</h2>
        </div>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 max-w-6xl mx-auto">
          {TREATMENT_OPTIONS.map((item) => (
            <div key={item.title} className="bg-white p-8 border border-[#E8E2D9]">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 bg-gold/10 flex items-center justify-center flex-shrink-0" aria-hidden>
                  <item.icon className="w-6 h-6 text-gold" />
                </div>
                <div>
                  <h3 className="font-serif text-xl text-premium-purple mb-4 font-semibold tracking-tight">{item.title}</h3>
                  <p className="text-deep-navy/70 leading-relaxed">{item.body}</p>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function HowPerformedSection() {
  return (
    <section className="bg-warm-white py-14 md:py-20 border-t border-deep-navy/[0.06]" data-testid="how-performed-section">
      <div className="container mx-auto px-4 md:px-8 max-w-7xl">
        <div className="grid lg:grid-cols-[1fr_2fr] gap-10 md:gap-16 items-start">
          <div>
            <p className="text-xs font-bold tracking-[0.2em] text-gold uppercase mb-3">The Procedure</p>
            <h2 className="font-serif text-3xl md:text-4xl text-premium-purple leading-tight">How Wart Removal is Performed</h2>
          </div>
          <div className="space-y-6 text-deep-navy/70 leading-relaxed">
            <p>
              The procedure takes 10 to 30 minutes depending on the number of warts and technique used. No hospital admission is required. Local anesthetic cream or injection is applied before treatment depending on the location and technique. Plantar warts on the foot require a local anesthetic injection to the foot for pain-free treatment. Facial and hand warts are often treated with topical numbing cream alone.
            </p>
            <p>
              The selected technique is applied to each wart. For RF and electrocautery, the wart tissue is removed or destroyed in a single application per wart. For cryotherapy, the probe is applied for a controlled freeze time calibrated to the wart size and depth. For laser, the beam is applied systematically across the wart surface. A simple dressing or topical antibiotic is applied at completion.
            </p>
            <p>
              Patients return to normal activity the same day in most cases. Post-procedure hygiene instructions are provided to prevent reinfection and promote healing. Follow-up is scheduled to assess clearance and determine whether additional sessions are needed.
            </p>
          </div>
        </div>
      </div>
    </section>
  );
}

function RecoveryTimelineSection() {
  const milestones = [
    {
      period: "Days 1 to 3",
      description:
        "Mild redness and tenderness at the treated site. A scab or blister forms depending on the technique used. Normal and expected. Keep the area clean and avoid picking.",
      icon: Clock,
    },
    {
      period: "Days 5 to 7",
      description:
        "Healing begins. Scabs start to separate. For cryotherapy, the blister has deflated and a scab is forming. Continue wound care as instructed.",
      icon: Monitor,
    },
    {
      period: "Weeks 1 to 2",
      description:
        "Skin heals completely for most techniques. The treated area appears flat and normal. Follow-up assessment confirms clearance or identifies whether additional treatment is needed.",
      icon: CheckCircle2,
    },
    {
      period: "Weeks 2 to 4",
      description:
        "For cases requiring multiple sessions, the second treatment is typically scheduled at this interval once the first treatment site has fully healed.",
      icon: GraduationCap,
    },
  ];
  return (
    <section className="bg-premium-purple py-10 md:py-14" data-testid="recovery-timeline-section">
      <div className="container mx-auto px-4 md:px-8 max-w-7xl">
        <h2 className="font-serif text-3xl md:text-4xl text-white mb-12 text-center">Recovery Timeline</h2>
        <div className="hidden md:block relative">
          <div className="absolute top-16 left-0 right-0 h-0.5 bg-deep-navy/10" />
          <div className="grid grid-cols-4 gap-4">
            {milestones.map((milestone, index) => (
              <div key={milestone.period} className="relative" data-testid={`timeline-milestone-${index}`}>
                <div className="flex justify-center mb-3">
                  <milestone.icon className="w-6 h-6 text-deep-navy/40" />
                </div>
                <div className="w-8 h-8 rounded-full bg-gold flex items-center justify-center mx-auto mb-4 relative z-10">
                  <div className="w-3 h-3 rounded-full bg-warm-white" />
                </div>
                <div className="text-center">
                  <h4 className="font-serif text-lg text-premium-purple mb-2">{milestone.period}</h4>
                  <p className="text-sm text-deep-navy/60 leading-relaxed">{milestone.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
        <div className="md:hidden relative pl-12">
          <div className="absolute top-0 bottom-0 left-4 w-0.5 bg-deep-navy/10" />
          <div className="space-y-8">
            {milestones.map((milestone, index) => (
              <div key={milestone.period} className="relative" data-testid={`timeline-mobile-${index}`}>
                <div className="absolute -left-12 top-0 flex flex-col items-center">
                  <milestone.icon className="w-5 h-5 text-deep-navy/40 mb-1" />
                  <div className="w-8 h-8 rounded-full bg-gold flex items-center justify-center">
                    <div className="w-3 h-3 rounded-full bg-warm-white" />
                  </div>
                </div>
                <div className="pl-4">
                  <h4 className="font-serif text-lg text-premium-purple mb-2">{milestone.period}</h4>
                  <p className="text-sm text-deep-navy/60 leading-relaxed">{milestone.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}

function SpecialConsiderationsSection() {
  return (
    <section className="bg-[#FAFAF8] py-14 md:py-20 border-t border-deep-navy/[0.06]" data-testid="special-considerations-section">
      <div className="container mx-auto px-4 md:px-8 max-w-7xl">
        <div className="max-w-4xl">
          <p className="text-xs font-bold tracking-[0.2em] text-gold uppercase mb-3">What to Expect</p>
          <h2 className="font-serif text-3xl md:text-4xl text-premium-purple mb-8">Special Considerations for Wart Treatment</h2>
          <div className="space-y-8 text-deep-navy/70 leading-relaxed">
            <div>
              <h3 className="font-serif text-xl text-premium-purple mb-4 font-semibold tracking-tight">Children</h3>
              <p>
                Children develop warts frequently, particularly on the hands and feet, as their immune systems encounter HPV for the first time. Most warts in children eventually resolve without treatment as the immune system develops a response to the virus, though this can take months to years. For persistent, spreading, or painful warts, treatment is appropriate. Gentler techniques including topical salicylic acid and cryotherapy are typically used in children before more intensive clinic-based procedures. Treatment is adapted to the child&apos;s ability to cooperate and tolerate the procedure.
              </p>
            </div>
            <div>
              <h3 className="font-serif text-xl text-premium-purple mb-4 font-semibold tracking-tight">Diabetic Patients</h3>
              <p>
                Diabetic patients with plantar warts require careful assessment before treatment as wound healing on the foot is impaired by the peripheral vascular and neuropathic effects of diabetes. Self-treatment is not appropriate. Clinical treatment is performed with wound management protocols specific to diabetic foot care. Dr. Jain advises on the appropriate technique and post-procedure care for diabetic patients at the consultation.
              </p>
            </div>
            <div>
              <h3 className="font-serif text-xl text-premium-purple mb-4 font-semibold tracking-tight">Genital Warts</h3>
              <p>
                Genital warts are caused by specific HPV strains and are classified as a sexually transmitted infection requiring dedicated management distinct from warts on other body sites. They require specialist assessment to confirm diagnosis, exclude associated conditions, and select the appropriate treatment. Dr. Jain treats genital warts with the specialized approach this location requires and can advise on partner notification and HPV vaccination as part of a comprehensive management plan.
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

function WhoShouldSeekSection() {
  return (
    <section className="bg-warm-white py-14 md:py-20 border-t border-deep-navy/[0.06]" data-testid="who-should-seek-section">
      <div className="container mx-auto px-4 md:px-8 max-w-7xl">
        <div className="max-w-4xl mx-auto">
          <p className="text-xs font-bold tracking-[0.2em] text-gold uppercase mb-3 text-center">Am I a Candidate?</p>
          <h2 className="font-serif text-3xl md:text-4xl text-premium-purple mb-8 text-center">Who Should Seek Wart Treatment?</h2>
          <div className="space-y-6 text-deep-navy/70 leading-relaxed">
            <p>
              Any patient with warts that are painful, spreading, increasing in number, cosmetically bothersome, or resistant to home treatment is appropriate for professional consultation. There is no minimum size or duration requirement. Early treatment prevents spread to adjacent skin and to other people, is easier to execute when warts are smaller, and produces better cosmetic outcomes when warts are on visible areas.
            </p>
            <p>
              Patients who have attempted home treatment with over-the-counter salicylic acid products without success, or whose warts have persisted for more than 6 to 12 months without resolution, benefit from professional assessment and clinic-based treatment. Patients with warts in difficult locations, around the nails, on the face, or on the soles of the feet, should seek professional treatment rather than attempting home removal. Patients who are immunocompromised, including those on immunosuppressive medications, have a higher risk of extensive and treatment-resistant wart infection and should seek specialist assessment early.
            </p>
          </div>
        </div>
      </div>
    </section>
  );
}

function PatientResultsSection() {
  return <PatientTestimonials procedureName="Lipoma" />;
}

function FAQSection() {
  return (
    <section className="bg-[#FAFAF8] py-14 md:py-20 border-t border-deep-navy/[0.06]" data-testid="faq-section">
      <div className="container mx-auto px-4 md:px-8 max-w-7xl">
        <div className="text-center mb-10">
          <p className="text-xs font-bold tracking-[0.2em] text-gold uppercase mb-3">Common Questions</p>
          <h2 className="font-serif text-3xl md:text-4xl text-premium-purple">Frequently Asked Questions</h2>
        </div>
        <div className="max-w-3xl mx-auto">
          <Accordion type="single" collapsible className="w-full">
            {WART_FAQS.map((faq, index) => (
              <AccordionItem key={faq.question} value={`wart-${index}`} className="border-b border-deep-navy/10">
                <AccordionTrigger className="text-left font-serif text-lg text-premium-purple hover:no-underline hover:text-gold py-6 [&[data-state=open]>div>svg:first-child]:hidden [&[data-state=open]>div>svg:last-child]:block [&>svg]:hidden">
                  <span className="pr-4">{faq.question}</span>
                  <div className="flex-shrink-0">
                    <Plus className="w-5 h-5 text-gold" />
                    <Minus className="w-5 h-5 text-gold hidden" />
                  </div>
                </AccordionTrigger>
                <AccordionContent className="text-deep-navy/70 leading-relaxed pb-6">{faq.answer}</AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </div>
    </section>
  );
}

function ConsultationCTASection() {
  return (
    <section className="bg-premium-purple py-10 md:py-14" data-testid="consultation-cta-section">
      <div className="container mx-auto px-4 md:px-8 max-w-7xl text-center">
        <h2 className="font-serif text-3xl md:text-4xl text-warm-white mb-4">Book Your Wart Removal Consultation</h2>
        <p className="text-warm-white/70 mb-8 max-w-2xl mx-auto leading-relaxed">
          During your consultation, Dr. Jain examines the warts, confirms the diagnosis, and selects the most appropriate treatment technique for the wart type, location, and extent. Realistic expectations about the number of sessions and recurrence risk are discussed before treatment begins. You leave with a clear treatment plan and practical hygiene guidance to reduce spread during the treatment period.
        </p>
        <div className="flex flex-col sm:flex-row gap-4 justify-center mb-6">
          <Button
            asChild
            className="bg-gold text-white hover:bg-gold-hover border-none rounded-none px-8 py-6 text-base"
            data-testid="cta-book-consultation"
          >
            <a href={WHATSAPP_URL} target="_blank" rel="noopener noreferrer">
              Book Consultation
              <ArrowRight className="w-5 h-5 ml-2" />
            </a>
          </Button>
          <Button
          asChild
          variant="outline"
          className="border-warm-white text-warm-white hover:bg-warm-white hover:text-deep-navy rounded-none px-8 py-6 text-base"
          data-testid="cta-call"
        >
          <a href={TEL_URL}>
            <Phone className="w-5 h-5 mr-2" />
            Call {PHONE_DISPLAY}
          </a>
        </Button>
        </div>
      </div>
    </section>
  );
}

function RelatedProceduresSection() {
  const procedures = [
    {
      name: "Skin Tag Removal",
      description: "Removal of benign skin growths that are frequently confused with warts before diagnosis.",
      href: SKIN_TAG_PATH,
    },
    {
      name: "Corn Removal",
      description: "Treatment of painful foot growths that share location with plantar warts but require different management.",
      href: CORN_PATH,
    },
    {
      name: "Mole Removal",
      description: "Assessment and removal of pigmented skin lesions alongside wart treatment.",
      href: MOLE_PATH,
    },
  ];
  return (
    <section className="bg-warm-white py-10 md:py-14 border-t border-deep-navy/[0.06]" data-testid="related-procedures-section">
      <div className="container mx-auto px-4 md:px-8 max-w-7xl">
        <div className="text-center mb-10">
          <p className="text-xs font-bold tracking-[0.2em] text-gold uppercase mb-3">Related Procedures</p>
          <h2 className="font-serif text-3xl md:text-4xl text-premium-purple">Related Procedures</h2>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          {procedures.map((procedure) => (
            <div
              key={procedure.name}
              className="bg-white p-8 border border-[#E8E2D9] hover:shadow-lg transition-shadow duration-300 group"
            >
              <h3 className="font-serif text-xl text-premium-purple mb-3 font-semibold tracking-tight">{procedure.name}</h3>
              <p className="text-deep-navy/60 mb-6 leading-relaxed">{procedure.description}</p>
              <Link
                to={procedure.href}
                className="inline-flex items-center text-deep-navy font-medium hover:text-gold transition-colors"
              >
                Learn More →
                <ArrowRight className="w-4 h-4 ml-2 group-hover:translate-x-1 transition-transform" />
              </Link>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

export default function WartRemovalPage() {
  return (
    <>
      <SEO
        title="Wart Removal | Dr. Vicky Jain"
        description="Warts on hands, feet, face, or private areas causing pain or spreading. Dr. Vicky Jain provides safe, effective wart removal. Book your consultation today."
        canonical="/wart-removal-mumbai"
      />
      <WartJsonLd />
      <Breadcrumb />
      <HeroSection />
      <WhatAreSection />
      <DrJainApproachSection />
      <TypesOfWartsSection />
      <WartVsOthersSection />
      <SpreadPreventionSection />
      <TreatmentOptionsSection />
      <HowPerformedSection />
      <RecoveryTimelineSection />
      <SpecialConsiderationsSection />
      <WhoShouldSeekSection />
      <PatientResultsSection />
      <FAQSection />
      <ConsultationCTASection />
      <RelatedProceduresSection />
    </>
  );
}
